import React, { useEffect, useState } from 'react';
import Logo from '../../assets/logo.svg?react'
import LogoPlus from '../../assets/logoplus.svg?react'
import { useTranslation } from 'react-i18next';
import { IconMaterial } from '../shared/iconMaterial/IconMaterial';
import { useLocation, useNavigate } from 'react-router';
import { useDashboard } from '../../context/DashboardProvider';

interface SidebarItem {
    id: string;
    label: string;
    icon?: string;
    path?: string;
    slug?: string;
    children?: SidebarItem[];
}

const Sidebar: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeItem, setActiveItem] = useState('');
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const { state } = useLocation()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setLoading } = useDashboard()

    const handleItemClick = (item: SidebarItem) => {
        if (item.children) {
            setOpenDropdown(openDropdown === item.id ? null : item.id);
        } else if (item.path) {
            setActiveItem(item.id);
            navigate(item.path, {
                state: { slug: item.id }
            });
            setLoading(true);
        }
    };
    useEffect(() => {
        if (state?.slug) {
            setActiveItem(state.slug)
        }
    }, [state?.slug])

    const sidebarItems: SidebarItem[] = [
        { id: 'orders', label: t('sideBar.items.orders'), icon: 'all_inbox', path: '/orders' },
        { id: 'prescriptions', label: t('sideBar.items.prescriptions'), icon: 'receipt', path: '/prescriptions' },
        { id: 'medication', label: t('sideBar.items.med-directory'), icon: 'auto_stories', path: '/medicationdirectory' },
        { id: 'equipment', label: t('sideBar.items.equipment'), icon: 'route', path: '/devices' },
        { id: 'users', label: t('sideBar.items.users'), icon: 'people_alt', path: '/users' },
        {
            id: 'activity',
            label: t('sideBar.items.activity-log'),
            icon: 'access_time',
            children: [
                { id: 'his-logs', label: t('sideBar.items.his-logs'), path: '/activitylog/' },
                { id: 'device-logs', label: t('sideBar.items.device-logs'), path: '/activitylog/' },
                { id: 'menu-item', label: t('sideBar.items.menu-item'), path: '/activitylog/' },
            ]
        }
    ];

    return (
        <div className={`relative fixed top-0 left-0 h-screen bg-darkblue flex flex-col justify-between ${isCollapsed ? 'w-auto' : 'w-[210px]'}`}>
            <div className='w-full flex flex-col px-4 py-3 items-center justify-center'>
                <div className='w-full relative'>
                    <div className='flex'>
                        <div className='w-full flex gap-2 items-center p-2 rounded-lg bg-white-transparent'>
                            <LogoPlus width={17.5} height={17.5} />
                            {!isCollapsed && <Logo width={74} hanging={14} />}
                        </div>
                    </div>
                    
                </div>
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="absolute -right-2 z-10 bg-white rounded-full flex items-center justify-center"
                        >
                            <IconMaterial filled icon='keyboard_double_arrow_left' size={16} />
                        </button>
            </div>

            <div className='h-[1px] w-full bg-white-transparent' />

            <nav className="flex flex-col px-4 py-2 justify-between h-full overflow-hidden">
                <ul className="space-y-2">
                    {sidebarItems.map((item) => (
                        <li key={item.id}>
                            <button
                                onClick={() => handleItemClick(item)}
                                className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all font-montserrat 
                                    ${activeItem === item.id ? 'bg-blue-700/50 text-white' : 'text-blue-100 hover:bg-[#223D78] hover:text-white'}`}
                            >
                                {item.icon && (
                                    <IconMaterial
                                        filled
                                        icon={item.icon}
                                        size={20}
                                        iconColor="var(--tokens-text-secondary-text)"
                                    />
                                )}
                                {!isCollapsed && <span className="text-left text-xs">{item.label}</span>}
                            </button>

                            {item.children && openDropdown === item.id && !isCollapsed && (
                                <ul className="ml-8 space-y-1">
                                    {item.children.map((child) => (
                                        <li className='flex items-center' key={child.id}>
                                            {activeItem == child.id && <div className='w-2 h-2 rounded-full bg-primary-stroke' />}

                                            <button
                                                onClick={() => handleItemClick(child)}
                                                className={`w-full text-left p-1 rounded-md text-xs 
                                                    ${activeItem === child.id ? 'text-primary-stroke' : 'text-disabled'}`}
                                            >
                                                {child.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>

                <ul className="space-y-2 mb-4">
                    <li>
                        <a href={`https://${import.meta.env.VITE_BASE_URL}`}>
                            <button
                                className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all font-montserrat 
                                ${activeItem === 'settings' ? 'bg-blue-700/50 text-white' : 'text-blue-100 hover:bg-blue-700/30 hover:text-white'}`}
                            >
                                <IconMaterial
                                    filled
                                    icon={'settings'}
                                    className="cursor-pointer"
                                    size={20}
                                    iconColor="var(--tokens-text-secondary-text)"
                                />
                                {!isCollapsed && (
                                    <span className="text-sm text-xs font-medium">
                                        {t("settings")}
                                    </span>
                                )}
                            </button>
                        </a>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

export default Sidebar;
